import { useState } from "react";
import { Check } from "lucide-react";
import InteriorsQuoteModal from "@/pages/interiors/InteriorsQuoteModal";

const packages = [
  {
    name: "1 Bedroom",
    tagline: "Perfect start",
    price: "from £3,779",
    desc: "Fully furnished 1-bed package from the YPF 2026 collection, delivered and assembled in 7 working days.",
    features: [
      "Double bed, mattress & wardrobe",
      "Complete bedding & linen set",
      "3-seater sofa & accent chair",
      "Dining table & 4 chairs",
      "43\" Smart TV & coffee table",
      "Kitchen electrical pack",
      "Bathroom accessories pack",
      "Artwork, lamps & soft furnishings",
    ],
    highlight: false,
  },
  {
    name: "2 Bedrooms",
    tagline: "Most popular",
    price: "from £4,829",
    desc: "Our most popular package — a complete 2-bed furnished setup styled and delivered ready for guests.",
    features: [
      "Everything in 1 Bedroom, plus:",
      "Second bedroom fully furnished",
      "Additional bedding & linen set",
      "Expanded accessories pack",
      "Professional interior styling",
      "3-year warranty on all hard furniture",
      "Flexible finance options available",
    ],
    highlight: true,
  },
  {
    name: "3 Bedrooms",
    tagline: "Maximum returns",
    price: "from £5,869",
    desc: "Full 3-bed package with three furnished bedrooms, complete living and dining areas — ready to list.",
    features: [
      "Everything in 2 Bedrooms, plus:",
      "Third bedroom fully furnished",
      "Full accessories across all rooms",
      "Bespoke design service available",
      "Custom collection consultation",
    ],
    highlight: false,
  },
];

export default function InteriorsPackages() {
  const [showModal, setShowModal] = useState(false);

  return (
    <section id="packages" style={{ backgroundColor: "#F8F8F6", padding: "clamp(60px, 7vw, 96px) clamp(20px, 5vw, 80px)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>Packages</p>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em", lineHeight: 1.15, color: "#1A1A1A", margin: "0 auto 14px", maxWidth: 600 }}>
            Choose your level of done.
          </h2>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#666", lineHeight: 1.6, maxWidth: 520, margin: "0 auto" }}>
            Three packages, all fully installed. Need something bespoke? We tailor to your property and budget.
          </p>
        </div>

        <div className="packages-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, alignItems: "stretch" }}>
          {packages.map((pkg) => (
            <div
              key={pkg.name}
              style={{
                background: pkg.highlight ? "#112215" : "#fff",
                borderRadius: 16,
                padding: "32px 28px",
                display: "flex",
                flexDirection: "column",
                border: pkg.highlight ? "none" : "1px solid #EFEFED",
                boxShadow: pkg.highlight ? "0 16px 40px rgba(0,0,0,0.18)" : "none",
                position: "relative",
              }}
            >
              {pkg.highlight && (
                <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: "#fff", color: "#1A1A1A", fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, padding: "5px 14px", borderRadius: 100, letterSpacing: "0.04em" }}>
                  MOST POPULAR
                </div>
              )}
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: pkg.highlight ? "rgba(255,255,255,0.45)" : "#888", margin: 0, marginBottom: 6 }}>{pkg.tagline}</p>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 24, color: pkg.highlight ? "#fff" : "#1A1A1A", margin: 0, marginBottom: 8, letterSpacing: "-0.02em" }}>{pkg.name}</h3>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: pkg.highlight ? "#fff" : "#1A1A1A", margin: 0, marginBottom: 12 }}>{pkg.price}</p>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13.5, color: pkg.highlight ? "rgba(255,255,255,0.65)" : "#666", lineHeight: 1.6, margin: 0, marginBottom: 24 }}>{pkg.desc}</p>

              <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
                {pkg.features.map((f) => (
                  <div key={f} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", background: pkg.highlight ? "rgba(255,255,255,0.1)" : "#F3F3F1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                      <Check size={11} color={pkg.highlight ? "#fff" : "#1A1A1A"} strokeWidth={2.5} />
                    </div>
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13.5, color: pkg.highlight ? "rgba(255,255,255,0.85)" : "#444", lineHeight: 1.5 }}>{f}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setShowModal(true)}
                style={{
                  marginTop: 28,
                  background: pkg.highlight ? "#fff" : "transparent",
                  color: pkg.highlight ? "#1A1A1A" : "#1A1A1A",
                  border: pkg.highlight ? "none" : "1.5px solid #D5D2CE",
                  borderRadius: 8,
                  padding: "13px 20px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => {
                  if (pkg.highlight) e.currentTarget.style.background = "#F3F3F1";
                  else { e.currentTarget.style.borderColor = "#1A1A1A"; }
                }}
                onMouseLeave={e => {
                  if (pkg.highlight) e.currentTarget.style.background = "#fff";
                  else { e.currentTarget.style.borderColor = "#D5D2CE"; }
                }}
              >
                Request a quote
              </button>
            </div>
          ))}
        </div>

        <p style={{ textAlign: "center", fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888", marginTop: 32, marginBottom: 0 }}>
          Prices are indicative and depend on property size and scope.
        </p>
      </div>

      {showModal && <InteriorsQuoteModal onClose={() => setShowModal(false)} onSubmitted={() => setShowModal(false)} />}

      <style>{`
        @media (max-width: 900px) { .packages-grid { grid-template-columns: 1fr !important; max-width: 460px; margin: 0 auto; } }
      `}</style>
    </section>
  );
}